import type { ReactElement } from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useAuth, useAuthModal } from '@fetmode/app-core';
import { t } from '@fetmode/i18n';
import { Button } from '@fetmode/ui';

import { useCoinShop } from '../wallet/index.js';
import { PlayerMenuDock, type PlayerMenuDockItem } from './PlayerMenuDock.js';

/** Default tip → vibration tiers when model has no toy config. */
const DEFAULT_TOY_CONTROLS: ReadonlyArray<Omit<PlayerMenuDockItem, 'disabled'>> = [
  { id: 'toy-low', label: 'Low buzz', amountCoins: 10 },
  { id: 'toy-medium', label: 'Medium pulse', amountCoins: 25 },
  { id: 'toy-high', label: 'High pulse', amountCoins: 75 },
  { id: 'toy-wave', label: 'Wave pattern', amountCoins: 50 },
  { id: 'toy-earthquake', label: 'Earthquake', amountCoins: 100 },
  { id: 'toy-take-control', label: 'Take control (60s)', amountCoins: 150 },
] as const;

export interface ToyControlBarProps {
  modelUsername: string;
  canTip: boolean;
  coinBalance: number;
  isTipping: boolean;
  onSendTip: (amountCoins: number, message?: string) => Promise<boolean>;
  playerEl: HTMLElement | null;
  menuOpen: boolean;
  onMenuOpenChange: (open: boolean) => void;
}

export function ToyControlBar({
  modelUsername,
  canTip,
  coinBalance,
  isTipping,
  onSendTip,
  playerEl,
  menuOpen,
  onMenuOpenChange,
}: ToyControlBarProps): ReactElement {
  const { requireAuth } = useAuthModal();
  const { requireCoins } = useCoinShop();
  const { api } = useAuth();
  const [controls, setControls] = useState<ReadonlyArray<Omit<PlayerMenuDockItem, 'disabled'>>>(
    import.meta.env.DEV ? DEFAULT_TOY_CONTROLS : [],
  );
  const [toysEnabled, setToysEnabled] = useState(false);

  const loadToys = useCallback(async (): Promise<void> => {
    try {
      const result = await api.getModelToys(modelUsername);
      setToysEnabled(result.settings.enabled);
      const enabledControls = result.controls.filter((control) => control.enabled);
      if (enabledControls.length > 0) {
        setControls(
          enabledControls.map((control) => ({
            id: control.id,
            label: control.label,
            amountCoins: control.amountCoins,
          })),
        );
      } else {
        setControls(import.meta.env.DEV ? DEFAULT_TOY_CONTROLS : []);
      }
    } catch {
      setToysEnabled(false);
      setControls(import.meta.env.DEV ? DEFAULT_TOY_CONTROLS : []);
    }
  }, [api, modelUsername]);

  useEffect(() => {
    void loadToys();
  }, [loadToys]);

  const dockItems = useMemo(
    () =>
      controls.map((control) => ({
        ...control,
        disabled: isTipping || !canTip,
      })),
    [controls, isTipping, canTip],
  );

  const activateToy = (item: PlayerMenuDockItem): void => {
    requireAuth(() => {
      if (!requireCoins(coinBalance, item.amountCoins, () => {
        activateToy(item);
      })) {
        return;
      }
      void onSendTip(item.amountCoins, item.label).then((sent) => {
        if (sent) {
          onMenuOpenChange(false);
        }
      });
    }, 'toys');
  };

  if (!toysEnabled) {
    return <></>;
  }

  return (
    <>
      <Button
        label={t('common.show.toyControlButton')}
        variant="toys"
        onClick={() => {
          requireAuth(() => {
            onMenuOpenChange(true);
          }, 'toys');
        }}
      />

      {menuOpen && playerEl
        ? createPortal(
            <PlayerMenuDock
              titleId="toy-control-dock-title"
              title={t('common.show.toyControlTitle')}
              hint={t('common.show.toyControlHint')}
              emptyLabel={t('common.show.toyControlEmpty')}
              closeLabel={t('common.show.toyControlClose')}
              items={dockItems}
              onSelect={activateToy}
              onClose={() => {
                onMenuOpenChange(false);
              }}
            />,
            playerEl,
          )
        : null}
    </>
  );
}
